
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import type { ActiveShoppingCart, ActiveCartItem, Language, ShoppingCart, ShoppingCategory, UpdateActiveShoppingCartFn } from '../types';
import CartIcon from './icons/CartIcon';
import XIcon from './icons/XIcon';
import PencilIcon from './icons/PencilIcon';
import PlusIcon from './icons/PlusIcon'; 
import TrashIcon from './icons/TrashIcon';
import EraserIcon from './icons/EraserIcon';
import ConfirmationModal from './ConfirmationModal';
import SmartShoppingList from './SmartShoppingList';
import ChangeCategoryModal from './ChangeCategoryModal';
import { mergePillarOrder, buildActiveCartPillarIds, buildListPillars, resolveItemPillar } from '../utils/shoppingCategories';
import AddItemModal from './AddItemModal'; 
import { getTranslation, isRTL } from '../utils/translations';
import Eye from './icons/EyeIcon';
import EyeOff from './icons/EyeOffIcon';
import ChevronsDownIcon from './icons/ChevronsDownIcon';
import ChevronsUpIcon from './icons/ChevronsUpIcon';
import MergeIcon from './icons/MergeIcon';
import LayoutGridIcon from './icons/LayoutGridIcon';
import { formatConsolidatedQuantity } from '../utils/unitConversion';
import { setTotalQuantity, addManualQuantity } from '../utils/activeCartSources';
import { expandedSectionsFromCollapsed, resolveCollapsedPillarIds } from '../utils/activeCartUiState';

interface ActiveShoppingCartModalProps {
    isOpen: boolean;
    onClose: () => void;
    activeCart: ActiveShoppingCart | null;
    onUpdateCart: UpdateActiveShoppingCartFn;
    language?: Language;
    knownItems: { name: string; unit: string; isMeat: boolean; category?: string }[];
    onNewItemAdded: (name: string, unit: string, isMeat: boolean, category: string) => void;
    shoppingCarts?: ShoppingCart[];
    onUpdateShoppingCartTemplate?: (cart: ShoppingCart) => Promise<void>;
    categoryOverrides?: Record<string, string>;
    onUpdateCategoryKnowledge?: (itemName: string, newCategory: string) => Promise<void>;
    savedExpandedSections?: string[];
    hasSavedSectionState?: boolean;
    onSaveExpandedSections?: (expandedSections: string[]) => Promise<void>;
}

const ActiveShoppingCartModal: React.FC<ActiveShoppingCartModalProps> = ({ 
    isOpen, onClose, activeCart, onUpdateCart, language, knownItems, onNewItemAdded,
    categoryOverrides, onUpdateCategoryKnowledge, shoppingCarts = [],
    savedExpandedSections, hasSavedSectionState = false, onSaveExpandedSections,
}) => {
    const t = (key: any) => getTranslation(language, key);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [isChangingCategory, setIsChangingCategory] = useState(false);
    const [showClearCompletedConfirm, setShowClearCompletedConfirm] = useState(false);
    const [showEmptyCartConfirm, setShowEmptyCartConfirm] = useState(false);
    const [baseFontSize, setBaseFontSize] = useState(16);
    
    // Sections collapsed by default; user can expand individually or via toolbar
    const [collapsedPillars, setCollapsedPillars] = useState<Set<string>>(new Set());
    const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
    
    const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
    const [isCompletedHidden, setIsCompletedHidden] = useState(false);
    const [addItemCategory, setAddItemCategory] = useState<string | undefined>(undefined);
    const [undoItem, setUndoItem] = useState<{ id: string; name: string; wasDone: boolean } | null>(null);
    const undoTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        };
    }, []);

    const scrollRef = useRef<HTMLDivElement>(null);
    const wasOpenRef = useRef(false);
    const hasRestoredSectionsRef = useRef(false);
    const collapsedPillarsRef = useRef<Set<string>>(new Set());
    const savedExpandedSectionsRef = useRef(savedExpandedSections);
    const hasSavedSectionStateRef = useRef(hasSavedSectionState);
    savedExpandedSectionsRef.current = savedExpandedSections;
    hasSavedSectionStateRef.current = hasSavedSectionState;
    const allPillarIds = useMemo(() => buildActiveCartPillarIds(shoppingCarts), [shoppingCarts]);
    const [pillarOrder, setPillarOrder] = useState<string[]>(() => buildActiveCartPillarIds([]));
    const pillarReorderInFlightRef = useRef(false);
    const pillarOrderRef = useRef(pillarOrder);
    pillarOrderRef.current = pillarOrder;

    const savedCollectionOrderKey = activeCart?.collectionOrder?.join('|') ?? '';
    const shoppingCartsKey = useMemo(
        () => shoppingCarts.map((cart) => cart.id).join('|'),
        [shoppingCarts],
    );

    const persistExpandedSections = useCallback((collapsed: Set<string>) => {
        if (!onSaveExpandedSections) return;
        onSaveExpandedSections(expandedSectionsFromCollapsed(allPillarIds, collapsed));
    }, [allPillarIds, onSaveExpandedSections]);

    useEffect(() => {
        collapsedPillarsRef.current = collapsedPillars;
    }, [collapsedPillars]);

    // Restore section state only when the modal opens — not when user profile syncs.
    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setCollapsedPillars(
                resolveCollapsedPillarIds(
                    allPillarIds,
                    savedExpandedSectionsRef.current,
                    hasSavedSectionStateRef.current,
                ),
            );
            hasRestoredSectionsRef.current = true;
        }
        if (!isOpen) {
            hasRestoredSectionsRef.current = false;
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, allPillarIds]);

    const handleClose = () => {
        if (onSaveExpandedSections && hasRestoredSectionsRef.current) {
            persistExpandedSections(collapsedPillarsRef.current);
        }
        onClose();
    };

    useEffect(() => {
        const merged = mergePillarOrder(activeCart?.collectionOrder, shoppingCarts);
        const mergedKey = merged.join('|');

        if (pillarReorderInFlightRef.current) {
            if (mergedKey === pillarOrderRef.current.join('|')) {
                pillarReorderInFlightRef.current = false;
            }
            return;
        }

        setPillarOrder((prev) => (prev.join('|') === mergedKey ? prev : merged));
    }, [savedCollectionOrderKey, shoppingCartsKey]);

    if (!isOpen) return null;

    const items = activeCart?.items || [];
    const hasCompleted = items.some(i => i.done);
    const hasItems = items.length > 0;

    const handleUpdateItem = async (itemId: string, updates: Partial<ActiveCartItem>) => {
        if (!activeCart) return;

        if (updates.done !== undefined) {
            const item = activeCart.items.find(i => i.id === itemId);
            if (item) {
                setUndoItem({ id: itemId, name: item.name, wasDone: item.done });
                if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
                undoTimerRef.current = setTimeout(() => {
                    setUndoItem(null);
                }, 3000);
            }
        }

        await onUpdateCart((latest) => ({
            ...latest,
            items: (latest.items ?? []).map((item) =>
                item.id === itemId ? { ...item, ...updates } : item,
            ),
        }));
    };

    const handleUndo = async () => {
        if (!activeCart || !undoItem) return;
        await onUpdateCart((latest) => ({
            ...latest,
            items: (latest.items ?? []).map((item) =>
                item.id === undoItem.id ? { ...item, done: undoItem.wasDone } : item,
            ),
        }));
        setUndoItem(null);
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };

    const handleUpdateTotalQty = async (itemId: string, newTotal: number) => {
        if (!activeCart) return;
        await onUpdateCart((latest) => ({
            ...latest,
            items: (latest.items ?? []).map((item) =>
                item.id === itemId
                    ? { ...item, sources: setTotalQuantity(item.sources, newTotal) }
                    : item,
            ),
        }));
    };

    const handleBulkMarkDone = async (done: boolean) => {
        if (!activeCart || selectedIds.size === 0) return;

        const selectedSnapshot = new Set(selectedIds);
        const listPillars = buildListPillars(shoppingCarts);
        const pillarsToExpand = new Set<string>();

        activeCart.items.forEach((item) => {
            if (selectedSnapshot.has(item.id)) {
                pillarsToExpand.add(resolveItemPillar(item, categoryOverrides, listPillars));
            }
        });

        await onUpdateCart((latest) => {
            const items = latest.items ?? [];
            const hasMatches = items.some((item) => selectedSnapshot.has(item.id));
            if (!hasMatches) return latest;

            return {
                ...latest,
                items: items.map((item) =>
                    selectedSnapshot.has(item.id) ? { ...item, done } : item,
                ),
            };
        });

        if (done && pillarsToExpand.size > 0) {
            setCollapsedPillars((prev) => {
                const next = new Set(prev);
                pillarsToExpand.forEach((pillarId) => next.delete(pillarId));
                return next;
            });
        }

        setSelectedIds(new Set());
        setSelectionMode(false);
    };

    const handleBulkMerge = async () => {
        if (!activeCart || selectedIds.size < 2) return;

        const selectedSnapshot = new Set(selectedIds);

        await onUpdateCart((latest) => {
            const selectedItemsList = (latest.items ?? []).filter((item) => selectedSnapshot.has(item.id));
            if (selectedItemsList.length < 2) return latest;

            const targetItem = selectedItemsList[0];
            const sourceItems = selectedItemsList.slice(1);

            let allSources = [...targetItem.sources];
            sourceItems.forEach((item) => {
                allSources = [...allSources, ...item.sources];
            });

            const updatedTargetItem = {
                ...targetItem,
                done: false,
                sources: allSources,
            };

            return {
                ...latest,
                items: (latest.items ?? [])
                    .map((item) => (item.id === targetItem.id ? updatedTargetItem : item))
                    .filter((item) => !sourceItems.some((source) => source.id === item.id)),
            };
        });

        setSelectedIds(new Set());
        setSelectionMode(false);
    };

    const handleBulkChangeCategory = async (pillarId: string) => {
        if (!activeCart || selectedIds.size === 0 || !onUpdateCategoryKnowledge) return;

        setIsChangingCategory(true);
        try {
            const selectedItems = activeCart.items.filter((item) => selectedIds.has(item.id));
            const uniqueNames = [...new Set(selectedItems.map((item) => item.name))];

            for (const name of uniqueNames) {
                await onUpdateCategoryKnowledge(name, pillarId);
            }

            setShowCategoryModal(false);
            setSelectedIds(new Set());
            setSelectionMode(false);
        } finally {
            setIsChangingCategory(false);
        }
    };

    const handleBulkDelete = async () => {
        if (!activeCart || selectedIds.size === 0) return;
        const selectedSnapshot = new Set(selectedIds);

        await onUpdateCart((latest) => ({
            ...latest,
            items: (latest.items ?? []).filter((item) => !selectedSnapshot.has(item.id)),
        }));

        setSelectedIds(new Set());
        setSelectionMode(false);
        setShowDeleteConfirm(false);
    };

    const handleClearCompleted = async () => {
        if (!activeCart) return;
        await onUpdateCart((latest) => ({
            ...latest,
            items: (latest.items ?? []).filter((item) => !item.done),
        }));
        setShowClearCompletedConfirm(false);
    };

    const handleEmptyCart = async () => {
        if (!activeCart) return;
        await onUpdateCart((latest) => ({
            ...latest,
            items: [],
        }));
        setShowEmptyCartConfirm(false);
        setSelectionMode(false);
    };

    const handleAddItem = async (name: string, category: string, isMeat: boolean, quantity: number, unit: string) => {
        if (!activeCart) return;

        const itemId = `${name.toLowerCase().trim()}_${unit}`;
        const newSource = { id: `manual-${Date.now()}`, originName: 'Manual', quantity: quantity, originType: 'manual' as const };
        
        await onUpdateCart((latest) => {
            const nextItems = [...(latest.items ?? [])];
            const existingIndex = nextItems.findIndex((item) => item.id === itemId);

            if (existingIndex > -1) {
                const existing = nextItems[existingIndex];
                nextItems[existingIndex] = {
                    ...existing,
                    done: false,
                    sources: addManualQuantity(existing.sources, quantity, 'Manual'),
                };
            } else {
                nextItems.push({
                    id: itemId,
                    name: name.trim(),
                    unit,
                    isMeat,
                    done: false,
                    category: category as ShoppingCategory,
                    sources: [newSource],
                });
            }

            return { ...latest, items: nextItems };
        });

        // Save to history/misc list for future autocomplete
        onNewItemAdded(name, unit, isMeat, category);
    };

    const togglePillar = (id: string) => {
        setCollapsedPillars(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleItemExpansion = (id: string) => {
        setExpandedItemIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleReorderPillars = async (newOrder: string[]) => {
        if (!activeCart) return;
        const finalOrder = mergePillarOrder(newOrder, shoppingCarts);

        pillarReorderInFlightRef.current = true;
        pillarOrderRef.current = finalOrder;
        setPillarOrder(finalOrder);
        try {
            await onUpdateCart((latest) => ({
                ...latest,
                collectionOrder: finalOrder,
            }));
        } catch (error) {
            pillarReorderInFlightRef.current = false;
            const restored = mergePillarOrder(activeCart.collectionOrder, shoppingCarts);
            pillarOrderRef.current = restored;
            setPillarOrder(restored);
            console.error('Failed to reorder cart categories', error);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center sm:p-4" onClick={handleClose}>
            <div className="bg-white dark:bg-slate-950 w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[3rem] shadow-2xl flex flex-col overflow-hidden sm:border border-white/20 dark:border-slate-800 transition-colors relative" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="p-4 sm:p-5 bg-white dark:bg-slate-900 border-b dark:border-slate-800 flex justify-between items-center shrink-0 gap-3">
                    <div className="flex items-center gap-3 flex-shrink-0 flex-grow min-w-0">
                        <div className="p-2.5 bg-brand-sage/10 text-brand-sage dark:bg-brand-sage/20 rounded-2xl flex items-center justify-center shrink-0">
                            <CartIcon className="w-6 h-6" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight leading-none mb-1 truncate">{t('activeCart')}</h3>
                            <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none">
                                {items.filter(i => i.done).length} / {items.length} {t('itemsLabel')}
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2 sm:gap-2 flex-shrink-0">
                        <button 
                            onClick={() => setSelectionMode(!selectionMode)} 
                            className={`p-2 sm:p-2.5 rounded-full sm:rounded-2xl transition-all ltr:order-5 rtl:order-2 ${selectionMode ? 'bg-sky-600 text-white' : 'text-slate-300 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 bg-slate-50 dark:bg-slate-800'}`}
                        >
                            <PencilIcon className="w-5 h-5" />
                        </button>

                        {hasCompleted && (
                            <button
                                onClick={() => setShowClearCompletedConfirm(true)}
                                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors ltr:order-4 rtl:order-3"
                                title={t('clearCompleted')}
                            >
                                <EraserIcon className="w-5 h-5" />
                            </button>
                        )}

                        {hasItems && (
                            <button
                                onClick={() => setShowEmptyCartConfirm(true)}
                                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors ltr:order-3 rtl:order-4"
                                title={t('emptyCart')}
                            >
                                <TrashIcon className="w-5 h-5" />
                            </button>
                        )}

                        <div className="w-px h-6 bg-slate-200 dark:bg-slate-800 mx-1 ltr:order-2 rtl:order-5"></div>

                        <button onClick={handleClose} className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-all ltr:order-1 rtl:order-6">
                            <XIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* View Toolbar - Enforced RTL Layout */}
                <div className="flex flex-row justify-between items-center px-4 py-2 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-800 shrink-0" dir="rtl">
                    {/* Right Side (Start in RTL): Font Control */}
                    <div className="flex items-center bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-full shadow-sm h-8 px-1" dir="ltr">
                        <button onClick={() => setBaseFontSize(Math.max(12, baseFontSize - 2))} className="w-7 h-full flex items-center justify-center text-slate-400 dark:text-slate-500 font-bold hover:text-slate-800 dark:hover:text-slate-200 transition-colors">-</button>
                        <div className="w-6 h-full flex items-center justify-center text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase select-none">A</div>
                        <button onClick={() => setBaseFontSize(Math.min(24, baseFontSize + 2))} className="w-7 h-full flex items-center justify-center text-slate-400 dark:text-slate-500 font-bold hover:text-slate-800 dark:hover:text-slate-200 transition-colors">+</button>
                    </div>

                    {/* Center: Add Item Action Button & Hide Done Toggle */}
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => {
                                setAddItemCategory(undefined);
                                setIsAddItemModalOpen(true);
                            }}
                            className="w-9 h-9 rounded-full bg-brand-orange hover:bg-orange-600 active:scale-95 text-white flex items-center justify-center shadow-md shadow-brand-orange/20 transition-all border border-white/10 shrink-0"
                            title={t('add') || 'Add'}
                        >
                            <PlusIcon className="w-5 h-5" />
                        </button>

                        <button
                            type="button"
                            onClick={() => setIsCompletedHidden(!isCompletedHidden)}
                            className={`w-9 h-9 flex items-center justify-center border rounded-full transition-colors cursor-pointer shrink-0 ${
                                isCompletedHidden
                                    ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-950/30 dark:border-blue-900/50 dark:text-blue-400'
                                    : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700'
                            }`}
                            title={isCompletedHidden ? t('showDone') : t('hideDone')}
                        >
                            {isCompletedHidden ? (
                                <Eye size={18} className="text-blue-500" />
                            ) : (
                                <EyeOff size={18} className="text-gray-500" />
                            )}
                        </button>
                    </div>

                    {/* Left Side (End in RTL): Expand/Collapse Actions */}
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCollapsedPillars(new Set(allPillarIds))}
                            className="w-8 h-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-all shadow-sm"
                            aria-label={t('collapseAll')}
                            title={t('collapseAll')}
                        >
                            <ChevronsUpIcon className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={() => setCollapsedPillars(new Set())}
                            className="w-8 h-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-all shadow-sm"
                            aria-label={t('expandAll')}
                            title={t('expandAll')}
                        >
                            <ChevronsDownIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div ref={scrollRef} className="flex-grow overflow-y-auto p-4 scrollbar-none bg-slate-50/50 dark:bg-slate-950/50 relative">
                    <SmartShoppingList 
                        items={items}
                        onToggleCheck={(id, checked) => handleUpdateItem(id, { done: checked })}
                        onUpdateTotalQty={handleUpdateTotalQty}
                        onUpdateItem={handleUpdateItem}
                        selectionMode={selectionMode}
                        selectedIds={selectedIds}
                        onToggleSelect={(id) => setSelectedIds(prev => {
                            const n = new Set(prev);
                            if (n.has(id)) n.delete(id); else n.add(id);
                            return n;
                        })}
                        language={language}
                        baseFontSize={baseFontSize}
                        collapsedPillars={collapsedPillars}
                        onTogglePillar={togglePillar}
                        expandedItemIds={expandedItemIds}
                        onToggleItemExpansion={toggleItemExpansion}
                        categoryOverrides={categoryOverrides}
                        undoItem={undoItem}
                        onUndo={handleUndo}
                        pillarOrder={pillarOrder}
                        onReorderPillars={selectionMode ? handleReorderPillars : undefined}
                        shoppingCarts={shoppingCarts}
                        onAddNewItemWithCategory={(category) => {
                            setAddItemCategory(category);
                            setIsAddItemModalOpen(true);
                        }}
                        hideDone={isCompletedHidden}
                    />
                </div>

                {selectionMode && (
                    <div className="p-4 sm:p-6 bg-white dark:bg-slate-900 border-t dark:border-slate-800 space-y-4 shrink-0 shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.05)]">
                        <div className="flex flex-col gap-3 animate-fade-in-up">
                            <div className="flex gap-2 sm:gap-3 flex-wrap">
                                <button onClick={() => setSelectionMode(false)} className="flex-1 min-w-[70px] py-3 sm:py-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 font-bold rounded-2xl text-xs uppercase tracking-widest transition-all border border-slate-200 dark:border-slate-700">{t('cancel')}</button>

                                {onUpdateCategoryKnowledge && (
                                    <button
                                        onClick={() => setShowCategoryModal(true)}
                                        disabled={selectedIds.size === 0}
                                        className="flex-1 min-w-[100px] py-3 sm:py-4 bg-indigo-600 text-white font-black rounded-2xl text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 dark:shadow-none disabled:opacity-20 flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors"
                                    >
                                        <LayoutGridIcon className="w-4 h-4" />
                                        {t('changeCategory')}
                                    </button>
                                )}

                                <button onClick={() => handleBulkMarkDone(true)} disabled={selectedIds.size === 0} className="flex-1 min-w-[70px] py-3 sm:py-4 bg-brand-sage text-white font-black rounded-2xl text-xs uppercase tracking-widest shadow-xl shadow-brand-sage/20 disabled:opacity-20">{t('markDone')}</button>
                                
                                {selectedIds.size >= 2 && (
                                    <button 
                                        onClick={handleBulkMerge} 
                                        className="flex-1 min-w-[70px] py-3 sm:py-4 bg-sky-600 text-white font-black rounded-2xl text-xs uppercase tracking-widest shadow-xl shadow-sky-200 dark:shadow-none flex items-center justify-center gap-2 hover:bg-sky-700 transition-colors"
                                    >
                                        <MergeIcon className="w-4 h-4" />
                                        {t('unite')}
                                    </button>
                                )}

                                <button onClick={() => setShowDeleteConfirm(true)} disabled={selectedIds.size === 0} className="flex-1 min-w-[70px] py-3 sm:py-4 bg-red-600 text-white font-black rounded-2xl text-xs uppercase tracking-widest shadow-xl shadow-red-200 dark:shadow-none disabled:opacity-20">{t('delete')}</button>
                            </div>
                        </div>
                    </div>
                )}


            </div>

            <ChangeCategoryModal
                isOpen={showCategoryModal}
                onClose={() => !isChangingCategory && setShowCategoryModal(false)}
                onSelect={handleBulkChangeCategory}
                shoppingCarts={shoppingCarts}
                itemCount={selectedIds.size}
                language={language}
            />

            <AddItemModal 
                isOpen={isAddItemModalOpen} 
                onClose={() => {
                    setIsAddItemModalOpen(false);
                    setAddItemCategory(undefined);
                }}
                onAdd={handleAddItem} 
                knownItems={knownItems} 
                language={language}
                categoryOverrides={categoryOverrides}
                initialCategory={addItemCategory}
            />

            <ConfirmationModal isOpen={showDeleteConfirm} title={t('deleteItems')} message={t('removeUserConfirm')} confirmText={t('delete')} cancelText={t('cancel')} onConfirm={handleBulkDelete} onCancel={() => setShowDeleteConfirm(false)} />
            <ConfirmationModal isOpen={showClearCompletedConfirm} title={t('clearCompleted')} message={t('clearCompletedConfirm')} confirmText={t('ok')} cancelText={t('cancel')} onConfirm={handleClearCompleted} onCancel={() => setShowClearCompletedConfirm(false)} />
            <ConfirmationModal isOpen={showEmptyCartConfirm} title={t('emptyCart')} message={t('emptyCartConfirm')} confirmText={t('ok')} cancelText={t('cancel')} onConfirm={handleEmptyCart} onCancel={() => setShowEmptyCartConfirm(false)} />
        </div>
    );
};

export default ActiveShoppingCartModal;
